/*
 * cf-one-price.js — Click Fencing
 * 2026-09-01 (v4)
 *
 * WHY THIS EXISTS
 * Pete, 2026-08-27: "we need to make sure what they see on the instant pricing is
 * what they pay at the checkout. Also, if they choose supply and install they will
 * book and pay a site inspection." Then, after the first cut went live:
 * "instant quote and breakdown should always match. the breakdown determines the
 * instant quote", "the page for each calculator is too hectic. get rid of the
 * inspection stuff", and "i just went to book a site inspection only and it wanted
 * me to pay the $7k from the fence i just put in."
 *
 * THEN, 2026-09-01: "i want you to remove the fee for booking a site inspection.
 * it wont need to go through the shopify store. it will now just send an email to
 * the enquiries." That retires the whole paid half of this file — see 2. below.
 *
 * WHAT IT DOES
 *
 * 1. THE BREAKDOWN DETERMINES THE HEADLINE. The big green figure and the itemised
 *    breakdown disagreed on the same screen: flat top 6000mm DIY read $944.13
 *    against $580.11, the same run on Supply & Install read $2,425.00 against
 *    $2,060.98 (the same +$364.02 in both modes), blade with a gate read +$92.16 at
 *    every length, and glass read exactly +10% on every config because the headline
 *    still grossed up a total that has included GST since the 2026-08-22 cutover.
 *    PerfMesh alone agreed. The breakdown total is what reaches the saved quote, the
 *    review page and the cart - flat top's $580.11 and $2,060.98 are both exactly
 *    what quote.grandTotal carries - so the headline is now written from it.
 *
 *    Source: the LAST VISIBLE "Total" on the page. On the configure step that is the
 *    breakdown's own grand total; on the review step it is the .breakdown-total
 *    block; on Supply & Install both already include the installation line. A zero
 *    is never written over a real figure, because the breakdown re-renders empty for
 *    a beat on every recalculation.
 *
 * 2. SUPPLY & INSTALL BOOKS A FREE INSPECTION, AND PAYS NOTHING. It used to check
 *    out for the full install estimate as a $0.01 balance line; then (2026-08-27) it
 *    checked out for a $99 inspection fee instead. As of 2026-09-01 there is no
 *    charge at all and no Shopify order: the customer picks a day and time, their
 *    details and their whole fence design go to the CRM worker's /enquiry endpoint
 *    with the drawing attached, an email lands at enquiries@clickfencing.com.au, and
 *    the customer gets a confirmation panel without ever leaving the page.
 *
 *    Mechanically: quote-review.js's own submit handler still runs and still posts
 *    the enquiry + PDF (that is the part we want). What it does NEXT - clear the
 *    cart, add the quote line, redirect to /checkout - is halted for Supply &
 *    Install by intercepting the first fetch in that chain and never settling it,
 *    so the chain simply stops before the redirect. Nothing in quote-review.js is
 *    modified; the confirmation is driven off the /enquiry response instead, so the
 *    customer is only ever told "booked" once the CRM has actually answered ok.
 *
 * 3. NOTHING GETS CHARGED WITH AN INSPECTION, ANYWHERE. The standalone booking page
 *    (/pages/book-a-site-inspection) no longer touches the cart at all - it posts
 *    straight to the CRM worker - so the old "clear the cart first" hook is gone.
 *
 * 4. ONE STORY, EVERYWHERE. The inspection is free. Any leftover "$99"/"$149"
 *    inspection prose baked into the four large calculator templates is rewritten by
 *    snippets/cf-inspection-offer.liquid, which remains the single place that copy
 *    lives.
 *
 * 5. The calculator pages carry no inspection promo and no extra caption - Pete asked
 *    for them to be less hectic, and the inspection wording belongs on the review
 *    page where the choice is actually made.
 *
 * SAFETY
 * Every entry point is wrapped. Worst case is "no correction applied" - this file
 * must never be able to break a calculator or block a submission.
 */
(function () {
  'use strict';
  if (window.__cfOnePriceInstalled) return;
  window.__cfOnePriceInstalled = 1;

  var LOG = '[cf-one-price]';
  var PHONE = '0497 701 834';

  var path = String(location.pathname || '');
  var onReview     = /\/pages\/quote-review/i.test(path);
  var onCalculator = /\/pages\/(glass-fencing|blade-fence-calculator|flat-top-fence-calculator|perfmesh-fence)/i.test(path);
  if (!onReview && !onCalculator) return;

  /* ---- helpers ---------------------------------------------------------- */

  function money(cents) {
    var n = (Number(cents) || 0) / 100;
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function centsFromText(t) {
    var m = String(t == null ? '' : t).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
    return m ? Math.round(parseFloat(m[1]) * 100) : null;
  }

  /* ---- A. Calculator pages ---------------------------------------------- */

  /* Pete: "the page for each calculator is too hectic. get rid of the inspection
     stuff." cf-inspection-offer.liquid renders site-wide; it stays everywhere else
     and is hidden only on the four calculators. */
  function hideInspectionPromo() {
    if (document.getElementById('cf-one-price-css')) return;
    var st = document.createElement('style');
    st.id = 'cf-one-price-css';
    st.textContent = '.cf-inspect{display:none !important}';
    (document.head || document.documentElement).appendChild(st);
  }

  /* The last VISIBLE "Total" and the amount beside it. Verified against
     quote.grandTotal on all four calculators, in both pricing modes. */
  function readBreakdownCents() {
    var all = document.querySelectorAll('*');
    var last = null;
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.children.length === 0 && el.offsetParent && /^\s*Total\s*$/i.test(el.textContent || '')) last = el;
    }
    if (last && last.nextElementSibling) return centsFromText(last.nextElementSibling.textContent);
    return null;
  }

  function syncCalculator() {
    hideInspectionPromo();

    /* The green figure sits in a fixed-height flex column; anything appended to it
       overlaps the heading. Nothing is ever added to that panel - only its text is
       rewritten. Any caption left by an earlier build is removed. */
    var stale = document.querySelectorAll('.cf-one-price-note');
    for (var s = 0; s < stale.length; s++) stale[s].parentNode.removeChild(stale[s]);

    var ids = ['instant-quote', 'final-quote'];
    var shown = [];
    for (var i = 0; i < ids.length; i++) {
      var el = document.getElementById(ids[i]);
      if (el && el.offsetParent) shown.push(el);
    }
    if (!shown.length) return;

    var cents = readBreakdownCents();
    if (cents == null || cents <= 0) return;
    var want = money(cents);
    lastHeadline = want;
    for (var j = 0; j < shown.length; j++) {
      if (shown[j].textContent.trim() !== want) shown[j].textContent = want;
    }
  }

  /* THE PRICE SQUARE FLICKERED WHEN ADD TO CART WAS PRESSED. 2026-09-02.

     Pete: "when i pressed the add to cart button the price squre flickered a higher price.
     please check the reason for this and fix it so prices are solid and dont change on
     customers when they go to cart review."

     Reproduced and recorded on a 6000mm glass run with delivery on, watching #final-quote
     through the click:

         beforeClick   $1,094.87
         headline->    $1094.87      <- the calculator's own write, no thousands separator
         headline->    $1,094.87     <- this file reformatting it

     The VALUE never changed - $995.87 goods + $99 delivery both times, and quote-review
     landed on the same $1,094.87. What moved was the comma: the calculator writes the raw
     figure on its recalculation and this file only corrected the formatting on its next
     tick, up to 150ms later. Every digit shifts one place when the comma appears, which on a
     big green number reads exactly like the price changing.

     Fixed by never letting the unformatted version reach the screen. A MutationObserver bound
     to the quote elements themselves fires as a microtask BEFORE the next paint, so the
     correction lands in the same frame.

     IT ONLY SUPPRESSES A RE-FORMAT, NEVER A REAL CHANGE. The guard is that the numbers must
     be equal: if the calculator writes a genuinely different figure, this stands aside and
     the normal tick recomputes it from the breakdown. A price that really has changed must
     always be allowed through - the customer needs to see that. */
  var lastHeadline = '';

  function holdHeadlineFormat() {
    var ids = ['instant-quote', 'final-quote'];
    for (var i = 0; i < ids.length; i++) {
      var el = document.getElementById(ids[i]);
      if (!el || el.__cfHeld) continue;
      el.__cfHeld = true;
      try {
        new MutationObserver(function (recs) {
          try {
            var t = recs[0] && recs[0].target;
            var node = t && (t.nodeType === 1 ? t : t.parentElement);
            if (!node) return;
            var v = (node.textContent || '').trim();
            if (!lastHeadline || v === lastHeadline) return;
            /* Same money, different formatting - put ours back before it paints. */
            if (centsFromText(v) === centsFromText(lastHeadline)) node.textContent = lastHeadline;
          } catch (e) {}
        }).observe(el, { childList: true, characterData: true, subtree: true });
      } catch (e) {}
    }
  }

  /* ---- B. Quote review: Supply & Install books a FREE inspection --------- */

  /* Is the quote on screen priced Supply & Install? A fact about the QUOTE. */
  function reviewIsSupplyInstall() {
    var lab = document.getElementById('qr-pricing-label');
    return !!(lab && lab.textContent && lab.textContent.indexOf('Supply & Install') !== -1);
  }

  /* Is the customer BOOKING AN INSPECTION rather than buying? A fact about the ROUTE,
     and since 2026-09-02 no longer the same question as the one above.
     Pete: "Free site inspection button on DIY quotes." A DIY customer can now press
     "Book a free site inspection instead" too, and when they do everything in this file
     that makes an inspection work - the day and time fields, the halt that stops the
     cart chain, the retitled CRM enquiry, the confirmation panel - has to apply to a
     quote whose label still reads DIY.
     cf-si-route.js owns that flag and defaults it to 'buy', so the sale stays the
     default path and nothing can fall into inspection mode by accident. On the buy
     route this falls through to the label check, which is what keeps cf-si-route's
     own suppress() trick working. */
  function reviewIsInstall() {
    if (window.__cfSiRoute === 'inspect') return true;
    return reviewIsSupplyInstall();
  }

  function activeQuote() {
    var calcs = ['ullc', 'blade', 'flattop', 'perfmesh'];
    var pill = document.querySelector('.qr-quote-pill.is-active');
    var label = pill ? String(pill.textContent || '').trim() : '';
    var pool = [];
    for (var i = 0; i < calcs.length; i++) {
      var arr = [];
      try { arr = JSON.parse(localStorage.getItem(calcs[i] + '_quotes') || '[]') || []; } catch (e) {}
      if (Object.prototype.toString.call(arr) === '[object Array]') {
        for (var j = 0; j < arr.length; j++) if (arr[j] && arr[j].breakdown) pool.push(arr[j]);
      }
    }
    if (label) {
      for (var k = 0; k < pool.length; k++) {
        if (String(pool[k].quoteName || '').trim() === label) return pool[k];
      }
    }
    pool.sort(function (a, b) { return (Date.parse(b.generatedAt) || 0) - (Date.parse(a.generatedAt) || 0); });
    return pool[0] || null;
  }

  var MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  var DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  function buildSlotFields() {
    var form = document.getElementById('qr-enquire-form');
    if (!form || document.getElementById('cf-si-date')) return;
    var anchor = document.getElementById('qr-field-deliverydate-wrap') ||
                 form.querySelector('.qr-modal__actions');
    var wrap = document.createElement('div');
    wrap.id = 'cf-si-slot-wrap';
    wrap.style.cssText = 'display:none;grid-column:1 / -1;';
    wrap.innerHTML =
      '<div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">' +
        '<div class="qr-field"><label for="cf-si-date">Preferred inspection day</label>' +
          '<select id="cf-si-date"></select></div>' +
        '<div class="qr-field"><label for="cf-si-time">Preferred time</label>' +
          '<select id="cf-si-time">' +
            '<option value="">Select a time…</option>' +
            '<option>8:00 AM</option><option>9:00 AM</option><option>10:00 AM</option>' +
            '<option>11:00 AM</option><option>12:00 PM</option><option>1:00 PM</option>' +
            '<option>2:00 PM</option><option>3:00 PM</option>' +
          '</select></div>' +
      '</div>' +
      '<p class="cf-si-hint" style="margin:8px 0 0;font-size:1.3rem;color:#475569;line-height:1.45;"></p>';
    if (anchor && anchor.parentElement) anchor.parentElement.insertBefore(wrap, anchor);
    else form.appendChild(wrap);

    var sel = document.getElementById('cf-si-date');
    var ph = document.createElement('option');
    ph.value = ''; ph.textContent = 'Select a day…';
    sel.appendChild(ph);
    var d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3);
    var added = 0;
    while (added < 20) {
      var wd = d.getDay();
      if (wd >= 1 && wd <= 5) {
        var o = document.createElement('option');
        o.value = DAYS[wd] + ' ' + d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
        o.textContent = o.value;
        sel.appendChild(o);
        added++;
      }
      d.setDate(d.getDate() + 1);
    }
  }

  function headerNote(install) {
    var host = document.querySelector('.qr-header__total-wrap');
    if (!host) return;
    var n = host.querySelector('.cf-si-header-note');
    if (!install) { if (n) n.parentNode.removeChild(n); return; }
    if (!n) {
      n = document.createElement('p');
      n.className = 'cf-si-header-note';
      n.style.cssText = 'margin:8px 0 0;font-size:1.3rem;line-height:1.5;color:#a7f3d0;max-width:320px;';
      host.appendChild(n);
    }
    var want = 'Estimate — confirmed at your site inspection. Nothing to pay today: ' +
               'the inspection is free.';
    if (n.textContent !== want) n.textContent = want;
  }

  /* THE DELIVERY DATE FLICKER. Pete, 2026-09-02: "this panel is flickering hard", with the
     Your Details modal showing the inspection day/time AND the Preferred Delivery Date at the
     same time - two fields that are mutually exclusive.

     Measured on the live page, sampling every 100ms for 3s with the modal open:

         #qr-field-deliverydate-wrap   display none x18, shown x12
         #qr-pricing-label             "Supply & Install" x30   (so the MODE is stable)
         #cf-si-slot-wrap              shown x30

     So nothing was oscillating between install and DIY. quote-review.js rebuilds the form,
     the delivery field comes back visible because that is its default, and this file only
     hides it again on its next tick - up to 700ms later. Every rebuild buys another flash.

     Chasing it with a faster tick would only make the flash shorter. The fix is to stop it
     being a race: a class on <body> survives the form being rebuilt, and the CSS rule in
     cf-contrast-fixes.liquid hides the field at paint time, so a freshly rendered form is
     never briefly wrong. The JS below still runs and is still correct - it is now the
     fallback rather than the mechanism. */
  function markInspectionMode(on) {
    try {
      var b = document.body;
      if (!b) return;
      if (b.classList.contains('cf-si-inspection-mode') !== !!on) {
        b.classList.toggle('cf-si-inspection-mode', !!on);
      }
    } catch (e) {}
  }

  function syncReview() {
    var install = reviewIsInstall();
    var isSI = reviewIsSupplyInstall();
    markInspectionMode(install);
    /* The header note and the notice below say "Supply & Install", so they follow the
       QUOTE, not the route. A DIY customer booking an inspection must not be told their
       DIY kit price is a supply-and-install estimate. */
    headerNote(isSI);
    buildSlotFields();

    var btn  = document.getElementById('qr-enquire-btn');
    var sub  = document.getElementById('qr-modal-subtitle');
    var slot = document.getElementById('cf-si-slot-wrap');
    var hint = slot ? slot.querySelector('.cf-si-hint') : null;
    var submitBtn = document.querySelector('#qr-enquire-form button[type="submit"]');
    var insp = document.getElementById('qr-field-inspection-wrap');

    if (install) {
      /* THE PRIMARY BUTTON IS NOT WRITTEN HERE ANY MORE. 2026-09-02.

         Pete: "ive found a flicker. on the quote reviews in account, this book
         inspection button keeps flickering to continue to payment."

         It was two files fighting over one button, each on its own 700ms timer: this
         one set it to "Book Free Site Inspection →" and cf-si-route.js set it back to
         "Continue to Payment", forever. Worse than a flicker - the label was a race, so
         which button a customer pressed decided nothing reliable, and on the render Pete
         photographed this file had won and the screen offered TWO inspection buttons and
         no way to buy at all. That is the dead Supply & Install sale again.

         Pete settled it: "i think we change that button to continue to paymnet and keep
         the button below as book inspection." So the primary button is the SALE and
         cf-si-route.js is its single owner - it is the file that knows which route the
         customer chose. This one keeps everything else (the day and time fields, the
         halt, the CRM retitle, the confirmation) and stops touching the label.

         The fallback matters: if cf-si-route is ever not on the page, __cfSiRoute is
         undefined, nothing else would offer an inspection, and this reverts to the old
         behaviour rather than leaving a button that lies about what it does. */
      if (typeof window.__cfSiRoute !== 'string' &&
          btn && btn.textContent.trim() !== 'Book Free Site Inspection →') {
        btn.textContent = 'Book Free Site Inspection →';
      }
      if (sub) {
        var wantSub = 'We’ll attach your full fence design to the booking and send it straight ' +
                      'to our team. The inspection is free and there is nothing to pay.';
        if (sub.textContent !== wantSub) sub.textContent = wantSub;
      }
      if (slot) slot.style.display = '';
      if (hint) {
        var wantHint = 'We’ll confirm this slot with you, or call to reschedule if it’s taken. ' +
                       'The site inspection is free — no charge now or on the day.';
        if (hint.textContent !== wantHint) hint.textContent = wantHint;
      }
      if (submitBtn && submitBtn.textContent.indexOf('Book') !== 0) {
        submitBtn.textContent = 'Book my free inspection →';
      }
      if (insp) insp.style.display = 'none';
      /* Nothing is being delivered on this path - they are booking a visit. */
      var dw = document.getElementById('qr-field-deliverydate-wrap');
      if (dw) { dw.style.display = 'none'; dw.setAttribute('data-cf-hid', '1'); }
      var dd = document.getElementById('qr-f-deliverydate');
      if (dd) { dd.required = false; dd.value = ''; }
      var notice = document.getElementById('qr-si-required-notice');
      if (notice && isSI) {
        var wantNotice = 'Supply & Install starts with a site inspection. We come out, measure up and ' +
                         'price the job exactly — the inspection is completely free, and nothing is ' +
                         'charged through the website.';
        if (notice.textContent.trim() !== wantNotice) notice.textContent = wantNotice;
        notice.style.display = '';
      }
    } else {
      /* Same reason as above - cf-si-route owns this label whenever it is loaded. */
      if (typeof window.__cfSiRoute !== 'string' &&
          btn && btn.getAttribute('data-cf-si') === '1' && btn.textContent.indexOf('Book') === 0) {
        btn.textContent = 'Continue to Payment';
      }
      if (slot) slot.style.display = 'none';
      /* Give the delivery date back if a Supply & Install quote was viewed first -
         the customer is buying a kit now and chooses when it arrives. */
      var dwd = document.getElementById('qr-field-deliverydate-wrap');
      if (dwd && dwd.getAttribute('data-cf-hid') === '1') {
        dwd.style.display = '';
        dwd.removeAttribute('data-cf-hid');
      }
    }
    /* WRITE ONLY ON CHANGE. This was unconditional, and on a 700ms tick it was the whole
       of the quote page's idle churn - 2 DOM mutations a second on a page where nothing
       was happening, measured. Every other write in this function was already guarded
       this way; this one was missed. */
    if (btn) {
      var wantSi = install ? '1' : '0';
      if (btn.getAttribute('data-cf-si') !== wantSi) btn.setAttribute('data-cf-si', wantSi);
    }

    var cta = document.querySelector('.qr-cta__copy p');
    if (cta) {
      var wantCta = install
        ? 'Add your details and pick a day — we’ll come and measure up for free, then price the job exactly.'
        : 'Add your details, then we’ll take you straight to secure checkout.';
      if (cta.textContent.trim() !== wantCta) cta.textContent = wantCta;
    }
  }

  /* ---- The confirmation panel ------------------------------------------- */

  function modalBody() {
    var form = document.getElementById('qr-enquire-form');
    return form && form.parentElement ? form.parentElement : null;
  }

  function showBookedPanel(day, time) {
    var host = modalBody();
    var form = document.getElementById('qr-enquire-form');
    if (!host || document.getElementById('cf-si-booked')) return;
    if (form) form.style.display = 'none';
    var sub = document.getElementById('qr-modal-subtitle');
    if (sub) sub.style.display = 'none';

    var when = [day, time].filter(Boolean).join(' at ');
    var box = document.createElement('div');
    box.id = 'cf-si-booked';
    box.style.cssText = 'text-align:center;padding:18px 8px;';
    box.innerHTML =
      '<div style="width:54px;height:54px;line-height:54px;margin:0 auto 14px;border-radius:50%;' +
        'background:#16a34a;color:#fff;font-size:2.8rem;font-weight:700;">✓</div>' +
      '<h3 style="margin:0 0 10px;font-size:2.1rem;color:#1e3a5f;">Your free site inspection is booked in</h3>' +
      '<p style="margin:0 auto 10px;max-width:460px;font-size:1.5rem;line-height:1.55;color:#475569;">' +
        (when ? 'You asked for <strong>' + when + '</strong>. ' : '') +
        'Your fence design and drawing have gone through to our team with the booking.</p>' +
      '<p style="margin:0 auto 10px;max-width:460px;font-size:1.5rem;line-height:1.55;color:#475569;">' +
        'We’ll call to confirm the time, or offer the next closest slot if that one’s taken. ' +
        '<strong>Nothing has been charged.</strong></p>' +
      '<p style="margin:0;font-size:1.45rem;font-weight:600;color:#0b5f83;">Need us sooner? Call ' +
        '<a href="tel:' + PHONE.replace(/\s/g, '') + '" style="color:#0b5f83;">' + PHONE + '</a>.</p>';
    host.appendChild(box);
    try { box.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
  }

  function showBookedError(msg) {
    var err = document.getElementById('qr-modal-error');
    var form = document.getElementById('qr-enquire-form');
    if (form) form.style.display = '';
    if (err) {
      err.textContent = (msg || 'We couldn’t send your booking') +
                        ' — please try again, or call us on ' + PHONE + '.';
      err.style.display = 'block';
    }
    var submitBtn = document.querySelector('#qr-enquire-form button[type="submit"]');
    if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Book my free inspection →'; }
  }

  /* ---- Halting the checkout for Supply & Install ------------------------- */

  /* quote-review.js's saveDetailsAndGoToCheckout() is a promise chain:
       /cart/clear.js -> /cart/add.js -> /cart/update.js -> location = '/checkout'
     For Supply & Install there is nothing to buy, so the chain is stopped at its
     first link by returning a promise that never settles. No error is raised (the
     chain's .catch() would show "something went wrong"), the redirect never
     happens, and the page stays put while the CRM enquiry - already in flight from
     the same submit handler - completes and drives the confirmation panel. */
  function installCheckoutHalt() {
    var orig = window.fetch;
    if (typeof orig !== 'function') return;
    window.fetch = function (input, init) {
      try {
        var url = String((input && input.url) || input || '');
        if (/\/cart\/(clear|add|update)(\.js)?/.test(url) && reviewIsInstall()) {
          try { console.log(LOG, 'S&I: free inspection booking - checkout halted, nothing charged'); } catch (e) {}
          return new Promise(function () { /* deliberately never settles */ });
        }
      } catch (e) {
        try { console.warn(LOG, 'cart passthrough', e); } catch (e2) {}
      }
      return orig.apply(this, arguments);
    };
  }

  /* Pete, 2026-08-27: "for supply and install, the only thing charged will be the site
     inspection. if they made a fence the drawing and parts will come through to us with
     their inspection booking." Pete, 2026-09-01: nothing is charged at all now.

     quote-review.js already posts the WHOLE saved quote plus the drawing PDF to the CRM
     worker's /enquiry endpoint, and the worker files it against pricing_type = install
     with the full breakdown, the sections and the PDF in R2. This retitles an S&I
     submission as a site inspection booking, carries the chosen day and time, and leaves
     everything else - reference id, breakdown, sections, PDF - exactly as the worker
     already expects. It also watches the response, because that response is now the only
     thing that proves the booking landed. */
  function installCrmHook() {
    var orig = window.fetch;
    if (typeof orig !== 'function') return;
    window.fetch = function (input, init) {
      try {
        var url = String((input && input.url) || input || '');
        if (/\/enquiry(\?|$)/.test(url) && init && init.body && reviewIsInstall()) {
          var b = JSON.parse(String(init.body));
          if (b && b.quote) {
            var day  = (document.getElementById('cf-si-date') || {}).value || '';
            var time = (document.getElementById('cf-si-time') || {}).value || '';
            var totalEl = document.getElementById('qr-grand-total');
            var base = b.quote.quoteName || ('Quote ' + (b.quote.quoteNum || ''));
            /* A DIY customer can book an inspection too now, so the booking must not be
               filed as Supply & Install just because it is an inspection. The office
               needs to know which one turned up on the enquiry. */
            var siQuote = reviewIsSupplyInstall();
            var bookingLabel = siQuote ? 'Supply & Install site inspection'
                                       : 'DIY / Supply only site inspection';
            if (String(base).indexOf('SITE INSPECTION') !== 0) {
              var whenBit = [day, time].filter(Boolean).join(' ');
              b.quote.quoteName = 'SITE INSPECTION' + (whenBit ? ' - ' + whenBit : '') + ' - ' + base;
            }
            /* The CRM worker builds its email body from the breakdown we send it, so
               the booking is carried in as zero-value information rows at the top of
               the table. They never touch the total, and they are added ONLY to the
               copy posted to /enquiry - the saved quote and the cart are untouched. */
            if (Object.prototype.toString.call(b.quote.breakdown) === '[object Array]') {
              var info = [];
              function note(item) {
                info.push({ category: 'SITE INSPECTION BOOKING', item: item, sku: '',
                            quantity: 1, unitPrice: 0, totalPrice: 0, variantId: null });
              }
              note('Booking type: ' + bookingLabel);
              note('Preferred day: ' + (day || 'not specified'));
              note('Preferred time: ' + (time || 'not specified'));
              note('Paid today: NOTHING - the site inspection is free and did not go through the store');
              note((siQuote ? 'Estimated supply & install: ' : 'Quoted DIY / supply only total: ') +
                   (totalEl ? String(totalEl.textContent || '').trim() : '') +
                   ' - NOT charged, confirmed on site');
              var site = [b.customer && b.customer.address, b.customer && b.customer.suburb, b.customer && b.customer.postcode].filter(Boolean).join(', ');
              if (site) note('Site address: ' + site);
              note('Fence below is what they designed - confirm on site. Drawing attached.');
              b.quote.breakdown = info.concat(b.quote.breakdown);
            }
            b.quote.inspectionBooking = {
              bookingType: bookingLabel,
              pricingType: siQuote ? 'install' : 'diy',
              preferredDay: day,
              preferredTime: time,
              estimatedSupplyAndInstall: totalEl ? String(totalEl.textContent || '').trim() : '',
              quoteName: base,
              planRef: b.quoteReference || (b.quote && b.quote.planRef) || null
            };
            /* The worker stores quote.deliveryDate as the job's date field; on an install
               job it is unused, so the requested visit date lives there where Pete can see
               it in the CRM row. */
            if (day) b.quote.deliveryDate = day;
            b.customer = Object.assign({}, b.customer || {}, {
              bookingType: bookingLabel,
              preferredDay: day,
              preferredTime: time
            });
            try {
              console.log(LOG, 'CRM: free site inspection booking', b.quote.quoteName, day, time);
            } catch (e) {}

            /* The confirmation is driven off this response, so the customer is only
               told "booked" once the CRM has actually said ok. */
            return orig.call(this, input, Object.assign({}, init, { body: JSON.stringify(b) }))
              .then(function (res) {
                try {
                  if (res && res.ok) showBookedPanel(day, time);
                  else showBookedError('We couldn’t send your booking');
                } catch (e) {}
                return res;
              })
              .catch(function (err) {
                try { showBookedError('We couldn’t send your booking'); } catch (e) {}
                throw err;
              });
          }
        }
      } catch (e) {
        try { console.warn(LOG, 'crm passthrough', e); } catch (e2) {}
      }
      return orig.apply(this, arguments);
    };
  }

  function guardSlot() {
    var form = document.getElementById('qr-enquire-form');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      try {
        if (!reviewIsInstall()) return;
        var day = document.getElementById('cf-si-date');
        var time = document.getElementById('cf-si-time');
        if (day && time && (!day.value || !time.value)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          var err = document.getElementById('qr-modal-error');
          if (err) {
            err.textContent = 'Please choose a preferred day and time for your site inspection.';
            err.style.display = 'block';
          }
          (day.value ? time : day).focus();
        }
      } catch (err2) {}
    }, true);
  }

  /* ---- boot ------------------------------------------------------------- */

  function tick() {
    try {
      if (onCalculator) { syncCalculator(); holdHeadlineFormat(); }
      /* Once the confirmation is up, stop rewriting the modal underneath it. */
      if (onReview && !document.getElementById('cf-si-booked')) syncReview();
    } catch (e) {
      try { console.warn(LOG, e); } catch (e2) {}
    }
  }

  function start() {
    if (onReview) {
      /* Order matters: the CRM hook must wrap fetch AFTER the halt hook, so that a
         request is tested against /enquiry before it is tested against /cart/*. */
      try { installCheckoutHalt(); } catch (e) {}
      try { installCrmHook(); } catch (e) {}
      try { guardSlot(); } catch (e) {}
    }
    tick();
    try {
      var pending = null;
      new MutationObserver(function () {
        /* The body class is re-asserted SYNCHRONOUSLY on every batch, ahead of the debounce.
           It is two reads and at most one classList write, and it is what stops a rebuilt
           form painting the delivery date for a frame before the debounced tick catches up. */
        try { if (onReview && !document.getElementById('cf-si-booked')) markInspectionMode(reviewIsInstall()); } catch (e2) {}
        if (pending) return;
        pending = setTimeout(function () { pending = null; tick(); }, 150);
      }).observe(document.body, { childList: true, subtree: true, characterData: true });
    } catch (e) {}
    setInterval(tick, 700);
    try { console.log(LOG, 'installed v4 (free site inspection)'); } catch (e) {}
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
